/**
 * Keep in sync with BE constants in:
 * benchling/chemistry/schema/core/constants.py
 */
export const MOLECULE_LOT_SCHEMA_INTERFACE_ID = 'ssi_moleculelot';
export const MOLECULE_LOT_DISPLAY_NAME = 'Molecule Lot';

export const MOLECULE_LOT_FIELD_NAMES = {
  MOLECULE_LINK: {displayName: 'Molecule', systemName: 'molecule'},
  PURITY: {displayName: 'Purity', systemName: 'purity'},
  CONCENTRATION_MOLAR: {displayName: 'Concentration (molar)', systemName: 'concentration_molar'},
  CONCENTRATION_DENSITY: {
    displayName: 'Concentration (density)',
    systemName: 'concentration_density',
  },
} as const;

import type {SearchableUnitDropdownProps} from 'units/SearchableUnitDropdown';

/**
 * Returns a unit type filter predicate for a molecule lot concentration field,
 * restricting to units of the correct dimensional type.
 *
 * - concentration_molar → molarity (AMOUNT^1 · LENGTH^-3)
 * - concentration_density → mass concentration (MASS^1 · LENGTH^-3)
 *
 * Returns undefined for non-concentration fields (no filtering needed).
 */
export function getMoleculeLotUnitTypeFilter(
  fieldSystemName: string
): SearchableUnitDropdownProps['unitTypeFilter'] {
  if (fieldSystemName === MOLECULE_LOT_FIELD_NAMES.CONCENTRATION_MOLAR.systemName) {
    return (unitType) => matchesTerms(unitType.terms, MOLARITY_TERMS);
  }
  if (fieldSystemName === MOLECULE_LOT_FIELD_NAMES.CONCENTRATION_DENSITY.systemName) {
    return (unitType) => matchesTerms(unitType.terms, DENSITY_TERMS);
  }
  return undefined;
}

// Dimensional term constants (keep in sync with InventoryUnitUtil.ts)
const MOLARITY_TERMS: ReadonlyArray<{value: string; exponent: number}> = [
  {value: 'AMOUNT', exponent: 1},
  {value: 'LENGTH', exponent: -3},
];

const DENSITY_TERMS: ReadonlyArray<{value: string; exponent: number}> = [
  {value: 'MASS', exponent: 1},
  {value: 'LENGTH', exponent: -3},
];

function matchesTerms(
  actualTerms: Array<{value: string; exponent: number}>,
  expectedTerms: ReadonlyArray<{value: string; exponent: number}>
): boolean {
  if (actualTerms.length !== expectedTerms.length) {
    return false;
  }
  const normalized = actualTerms
    .map((t) => ({value: t.value, exponent: t.exponent}))
    .sort((a, b) => a.value.localeCompare(b.value) || a.exponent - b.exponent);
  const expected = [...expectedTerms].sort(
    (a, b) => a.value.localeCompare(b.value) || a.exponent - b.exponent
  );
  return normalized.every((t, i) => t.value === expected[i].value && t.exponent === expected[i].exponent);
}
